import { Copy, ExternalLink, Loader2, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { comuneByIstat } from '@sse/gazetteer';
import {
  BANDO_ACTIVITY_LABELS,
  BANDO_APPLICANT_LABELS,
  BUSINESS_STAGE_LABELS,
  type ApplicationForm,
  type BandoActivity,
  type BandoApplicant,
  type BusinessStage,
} from '@sse/schema/bando';
import { btnPrimary, btnSecondary, inputCls } from '../components/ui';
import { ApiError } from '../lib/api';
import { formatStamp } from '../lib/format';
import { useSession } from '../lib/session';

const STATUSES = [
  ['received', 'Ricevuta'],
  ['working', 'In lavorazione'],
  ['submitted', "Domanda presentata all'ente"],
  ['closed', 'Chiusa'],
] as const;
type Status = (typeof STATUSES)[number][0];

interface Check {
  eligible: boolean;
  summary: string;
  issues: string[];
  suggestions: string[];
  missing: string[];
}
interface Draft {
  title: string;
  summary: string;
  context: string;
  objectives: string[];
  activities: string[];
  impact: string;
  budget: Array<{ item: string; amount: number | null }>;
  checklist: string[];
}
interface Request {
  id: string;
  bandoId: string;
  bando: { title: string; issuer: string; deadline?: string };
  email: string;
  contact: { name?: string; kind?: string; organization?: string; phone?: string };
  /** tipo di domanda (senza: evento, le richieste di prima) */
  form?: ApplicationForm;
  application: Record<string, string | number | undefined>;
  answers: Record<string, boolean>;
  done: string[];
  check: Check;
  status: Status;
  note?: string;
  draft?: Draft;
  draftStatus?: 'pending' | 'ready' | 'error';
  createdAt: string;
  updatedBy?: string;
}

/** I campi della richiesta, con le etichette del tipo di domanda. */
const FIELDS: Record<ApplicationForm, Array<[string, string]>> = {
  evento: [
    ['applicant', 'Chi presenta la domanda'],
    ['activity', 'Tipo di evento'],
    ['organization', 'Ente'],
    ['event', 'Evento'],
    ['comuneId', 'Comune'],
    ['dateFrom', 'Dal'],
    ['dateTo', 'Al'],
    ['edition', 'Edizione'],
    ['audience', 'Pubblico atteso'],
    ['budget', 'Budget totale (€)'],
    ['requested', 'Contributo richiesto (€)'],
    ['needs', 'A cosa servono i fondi'],
    ['description', 'Descrizione'],
  ],
  impresa: [
    ['applicant', 'Chi presenta la domanda'],
    ['stage', "L'impresa è"],
    ['organization', 'Impresa'],
    ['event', 'Attività'],
    ['sector', 'Settore e ATECO'],
    ['comuneId', 'Comune della sede operativa'],
    ['dateFrom', 'Avvio previsto'],
    ['audience', 'Clienti'],
    ['budget', 'Spesa totale prevista (€)'],
    ['requested', 'Contributo richiesto (€)'],
    ['needs', 'Investimenti previsti'],
    ['description', 'Progetto d’impresa'],
  ],
  progetto: [
    ['applicant', 'Chi presenta la domanda'],
    ['activity', 'Cosa si finanzia'],
    ['organization', 'Ente'],
    ['event', 'Progetto'],
    ['comuneId', 'Comune'],
    ['dateFrom', 'Dal'],
    ['dateTo', 'Al'],
    ['audience', 'A chi si rivolge'],
    ['budget', 'Spesa totale (€)'],
    ['requested', 'Contributo richiesto (€)'],
    ['needs', 'A cosa servono i fondi'],
    ['description', 'Descrizione'],
  ],
};

/** Valore leggibile: etichette per chi, cosa e stato dell'impresa, nome del comune. */
const show = (k: string, v: string | number | undefined) =>
  k === 'applicant'
    ? (BANDO_APPLICANT_LABELS[v as BandoApplicant] ?? String(v))
    : k === 'activity'
      ? (BANDO_ACTIVITY_LABELS[v as BandoActivity] ?? String(v))
      : k === 'stage'
        ? (BUSINESS_STAGE_LABELS[v as BusinessStage] ?? String(v))
        : k === 'comuneId'
          ? (comuneByIstat(String(v))?.name ?? String(v))
          : String(v);

const euro = (n: number) =>
  new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n);

/** La bozza come testo, da incollare nel modulo dell'ente. */
const draftText = (d: Draft) =>
  [
    d.title,
    `Sintesi\n${d.summary}`,
    `Contesto e motivazioni\n${d.context}`,
    `Obiettivi\n${d.objectives.map((x) => `- ${x}`).join('\n')}`,
    `Attività\n${d.activities.map((x) => `- ${x}`).join('\n')}`,
    `Ricadute sul territorio\n${d.impact}`,
    `Piano economico\n${d.budget.map((b) => `- ${b.item}: ${b.amount === null ? '[DA COMPLETARE]' : euro(b.amount)}`).join('\n')}`,
  ].join('\n\n');

/**
 * Domande dei bandi (docs/07-admin-e-bot.md, "Bandi"): le richieste di chi organizza, già
 * controllate dall'AI (ammissibili), con i dati dell'evento, i consigli dati, lo stato (lo vede
 * chi organizza) e la nota; "Prepara la bozza con l'AI" scrive per il team la bozza della domanda.
 */
export function BandiRequests({ focus }: { focus: string | null }) {
  const { api } = useSession();
  const [items, setItems] = useState<Request[] | null>(null);
  const [open, setOpen] = useState<string | null>(focus);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<{ items: Request[] }>('/bandi/requests')
      .then((r) => setItems(r.items))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)));
  }, [api]);
  const replace = (r: Request) => setItems((xs) => xs?.map((x) => (x.id === r.id ? r : x)) ?? xs);

  if (error) return <p className="bg-danger-soft p-3 text-sm">{error}</p>;
  if (!items) return <p className="text-sm text-muted">Carico…</p>;
  if (!items.length) return <p className="text-sm text-muted">Nessuna richiesta.</p>;
  return (
    <ul className="flex flex-col gap-3">
      {items.map((r) => (
        <li key={r.id} className="border border-fg/15 bg-page">
          <button
            type="button"
            onClick={() => setOpen(open === r.id ? null : r.id)}
            className="flex w-full flex-wrap items-baseline justify-between gap-2 p-4 text-left hover:bg-surface"
          >
            <span className="min-w-0">
              <span className="font-medium">{String(r.application.event ?? '')}</span>
              <span className="text-sm text-fg/70"> · {r.bando.title}</span>
            </span>
            <span className="text-sm">
              {STATUSES.find(([k]) => k === r.status)?.[1]} · {formatStamp(r.createdAt)}
            </span>
          </button>
          {open === r.id && <RequestDetail r={r} onChange={replace} />}
        </li>
      ))}
    </ul>
  );
}

function RequestDetail({ r, onChange }: { r: Request; onChange: (r: Request) => void }) {
  const { api, config } = useSession();
  const [status, setStatus] = useState<Status>(r.status);
  const [note, setNote] = useState(r.note ?? '');
  const [msg, setMsg] = useState('');
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  // bozza in preparazione: si rilegge la richiesta ogni 5 secondi
  useEffect(() => {
    if (r.draftStatus !== 'pending') return;
    timer.current = setInterval(async () => {
      try {
        const x = await api<Request>(`/bandi/requests/${r.id}`);
        if (x.draftStatus !== 'pending') onChange(x);
      } catch {
        // al giro dopo
      }
    }, 5000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [api, r.id, r.draftStatus, onChange]);

  const save = async () => {
    try {
      onChange(
        await api<Request>(`/bandi/requests/${r.id}`, { method: 'PATCH', body: { status, note } }),
      );
      setMsg('Salvato: chi organizza vede il nuovo stato.');
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : String(e));
    }
  };
  const draft = async () => {
    try {
      await api(`/bandi/requests/${r.id}/draft`, { method: 'POST' });
      onChange({ ...r, draftStatus: 'pending' });
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : String(e));
    }
  };
  const c = r.check;
  const d = r.draft;
  return (
    <div className="flex flex-col gap-4 border-t border-fg/15 p-4 text-sm">
      <p>
        <span className="font-medium">Chi organizza:</span>{' '}
        {[r.contact.name, r.contact.organization, r.email, r.contact.phone]
          .filter(Boolean)
          .join(' · ')}
      </p>
      <p>
        <span className="font-medium">Bando:</span> {r.bando.title} ({r.bando.issuer})
        {r.bando.deadline ? ` · scade il ${r.bando.deadline}` : ''} ·{' '}
        <a
          href={`${config.siteUrl.replace(/\/$/, '')}/organizers/grants/${r.bandoId}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 underline"
        >
          pagina sul sito <ExternalLink className="size-3" aria-hidden />
        </a>
      </p>
      <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[max-content_1fr]">
        {FIELDS[r.form ?? 'evento']
          .filter(([k]) => r.application[k] !== undefined && r.application[k] !== '')
          .map(([k, l]) => (
            <div key={k} className="contents">
              <dt className="text-fg/70">{l}</dt>
              <dd className="whitespace-pre-line">{show(k, r.application[k])}</dd>
            </div>
          ))}
      </dl>
      <div className={`p-3 ${c.eligible ? 'bg-ok-soft' : 'bg-danger-soft'}`}>
        <p className="font-medium">
          Controllo AI: {c.eligible ? 'ammissibile' : 'non ammissibile'}
        </p>
        <p>{c.summary}</p>
        {c.suggestions.length > 0 && (
          <p className="mt-1">
            <span className="font-medium">Consigli dati:</span> {c.suggestions.join(' · ')}
          </p>
        )}
        {c.missing.length > 0 && (
          <p className="mt-1">
            <span className="font-medium">Dati mancanti:</span> {c.missing.join(' · ')}
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <label>
          Stato (lo vede chi organizza)
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as Status)}
            className={inputCls}
          >
            {STATUSES.map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label className="min-w-64 flex-1">
          Nota interna
          <input value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} />
        </label>
        <button type="button" className={btnPrimary} onClick={() => void save()}>
          Salva
        </button>
        {msg && <span>{msg}</span>}
      </div>
      <div className="flex flex-col gap-3 border-t border-fg/15 pt-3">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className={btnSecondary}
            disabled={r.draftStatus === 'pending'}
            onClick={() => void draft()}
          >
            {r.draftStatus === 'pending' ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Sparkles className="size-4" aria-hidden />
            )}{' '}
            {d ? 'Riscrivi la bozza con l’AI' : 'Prepara la bozza con l’AI'}
          </button>
          {r.draftStatus === 'pending' && <span className="text-muted">Circa un minuto…</span>}
          {r.draftStatus === 'error' && (
            <span className="text-danger">Bozza non riuscita: riprova.</span>
          )}
          {d && (
            <button
              type="button"
              className={btnSecondary}
              onClick={async () => {
                await navigator.clipboard.writeText(draftText(d));
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
            >
              <Copy className="size-4" aria-hidden /> {copied ? 'Copiata' : 'Copia la bozza'}
            </button>
          )}
        </div>
        {d && (
          <article className="flex flex-col gap-2 bg-surface p-3">
            <p className="font-medium">{d.title}</p>
            <p>{d.summary}</p>
            <p className="font-medium">Contesto</p>
            <p className="whitespace-pre-line">{d.context}</p>
            <p className="font-medium">Obiettivi</p>
            <ul className="list-disc pl-5">
              {d.objectives.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
            <p className="font-medium">Attività</p>
            <ul className="list-disc pl-5">
              {d.activities.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
            <p className="font-medium">Ricadute</p>
            <p className="whitespace-pre-line">{d.impact}</p>
            <p className="font-medium">Piano economico</p>
            <ul className="pl-0">
              {d.budget.map((b) => (
                <li key={b.item} className="flex justify-between gap-3">
                  <span>{b.item}</span>
                  <span className="tabular-nums">
                    {b.amount === null ? 'da completare' : euro(b.amount)}
                  </span>
                </li>
              ))}
            </ul>
            {d.checklist.length > 0 && (
              <>
                <p className="font-medium">Da verificare</p>
                <ul className="list-disc pl-5">
                  {d.checklist.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              </>
            )}
          </article>
        )}
      </div>
    </div>
  );
}
